import { useFormik } from "formik";
import * as Yup from "yup";
import { processAttachments } from "../lib/attachmentUtils";
import { uploadSingleFile } from "../api/service";
import { taskMinutesToHours } from "../components/projects/workDetailsForm/workTypeMapping";

export const useAddTaskForm = (defaultValue, onSubmit) => {
  const initialValues = {
    title: defaultValue?.title || "",
    task_description: defaultValue?.task_description || "",
    project: defaultValue?.project || null,
    taskGroup: defaultValue?.taskGroup || "",
    taskCategory: defaultValue?.taskCategory || "",
    taskFlow: defaultValue?.taskFlow || "",
    extraTaskWorkType: defaultValue?.extraTaskWorkType || "",
    taskMonth: defaultValue?.taskMonth || "",
    startDate: defaultValue?.startDate || "",
    dueDate: defaultValue?.dueDate || "",
    timeEstimate: defaultValue?.timeEstimate || "",
    priority: defaultValue?.priority || "Low",
    assignedTo: defaultValue?.assignedTo
      ? Array.isArray(defaultValue.assignedTo)
        ? defaultValue.assignedTo.map((user) => user._id || user)
        : [defaultValue.assignedTo._id || defaultValue.assignedTo]
      : [],
    copyOfDescription: defaultValue?.copyOfDescription || "",
    description: defaultValue?.description || "",
    requiresClientApproval: defaultValue?.requiresClientApproval || false,
    sequentialSubtasks: defaultValue?.sequentialSubtasks || false,
    requiresWorkLink: defaultValue?.requiresWorkLink || false,
    assetBankItem: "",
    // Left unset on edit until the picker loads, so an edit never clears them
    coveredTasks: defaultValue?.coveredTasks,
    campaign: defaultValue?.campaign?._id || defaultValue?.campaign || "",
    requiresCampaignReport: defaultValue?.requiresCampaignReport || false,
    customFields: defaultValue?.customFields || [],
    categoryFieldValues: defaultValue?.categoryFieldValues || [],
    subtasks: defaultValue?.subtasks || [],
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Name is required"),
    task_description: Yup.string(),
    project: Yup.string().nullable(),
    taskGroup: Yup.string().when("project", {
      is: (project) => project && project !== "" && project !== "other",
      then: (schema) => schema.required("Work type is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    extraTaskWorkType: Yup.string().when(["taskGroup", "project"], {
      is: (taskGroup, project) =>
        taskGroup === "extraTask" &&
        project &&
        project !== "" &&
        project !== "other",
      then: (schema) => schema.required("Work type is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    campaign: Yup.string().when("taskGroup", {
      is: "campaign",
      then: (schema) => schema.required("Select a campaign"),
      otherwise: (schema) => schema.nullable(),
    }),
    taskMonth: Yup.string()
      .required("Task month is required")
      .matches(/^\d{4}-\d{2}$/, "Task month must be in YYYY-MM format"),
    startDate: Yup.string().required("Start date is required"),
    dueDate: Yup.string().required("Due date is required"),
    timeEstimate: Yup.number().nullable(),
    priority: Yup.string().required("Priority is required"),
    assignedTo: Yup.array().min(1, "At least one assignee is required"),
    requiresClientApproval: Yup.boolean(),
    sequentialSubtasks: Yup.boolean(),
    requiresWorkLink: Yup.boolean(),
    requiresCampaignReport: Yup.boolean(),
    copyOfDescription: Yup.string(),
    description: Yup.string(),
    customFields: Yup.array().of(
      Yup.object().shape({
        label: Yup.string().required("Label is required"),
        value: Yup.string(),
      })
    ),
  });

  const handleSubmit = (values, formikBag) => {
    const minutes = values.timeEstimate;
    const timeEstimate =
      minutes === "" || minutes == null
        ? minutes
        : taskMinutesToHours(minutes);

    // Convert empty string project to null before submitting
    const processedValues = {
      ...values,
      project: values.project === "" ? null : values.project,
      timeEstimate,
    };

    onSubmit(processedValues, formikBag);
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
  });

  return formik;
};
